import type { UsageProjectionConfidence } from './billing-ar-forecast.js';
import type {
  CustomBreakdown,
  CustomBreakdownInput,
  DimensionSplit,
  DimensionSplitInput,
  SegmentSplit,
  SegmentSplitInput,
} from './classification.js';
import type { PaginatedResponse } from './common.js';
import type {
  ApAllocationType,
  ArInvoiceStatus,
  BillingContractAmendmentStatus,
  BillingContractAmendmentType,
  BillingContractStatus,
  BillingDayAnchor,
  BillingFrequency,
  BillingMode,
  BillingServiceNfIssuer,
  BillingServiceStatus,
  BillingTiming,
  ContractBillingCycleStatus,
  ContractBillingInvoiceDiffDecision,
  ContractBillingInvoiceDiffType,
  ContractItemServicePeriodRule,
  ContractMilestoneStatus,
  ContractRecognitionEvidenceKind,
  CustomerBalanceReason,
  CustomerBalanceStatus,
  CustomerBalanceType,
  CustomerEmailRole,
  IrrfNature,
  NfseIssTaxation,
  PaymentMethod,
  RenewalAdjustmentType,
  RenewalCalculationStatus,
  RevenueCycleNfTrigger,
  RevenueCycleSource,
  RevenueRecognitionMethod,
  ServiceCalculationMode,
  ServiceInvoicing,
  UsageRecordSource,
  UsageRecordStatus,
} from './generated/enums.js';

/**
 * Codes of the system-owned economic index catalog (sys_economic_index, ALC-1416).
 * `renewalIndex` on contracts/templates is FK-constrained to these; keep in sync
 * with the catalog seed migration when adding an index.
 */
export const ECONOMIC_INDEX_CODES = ['IGPM', 'IPCA', 'INPC', 'SELIC'] as const;

export type EconomicIndexCode = (typeof ECONOMIC_INDEX_CODES)[number];

/** Freshness state for a catalog index (ALC-1456). */
export const ECONOMIC_INDEX_FRESHNESS_STATES = ['FRESH', 'STALE', 'UNKNOWN'] as const;
export type EconomicIndexFreshnessState = (typeof ECONOMIC_INDEX_FRESHNESS_STATES)[number];

/** Last sync outcome persisted on sys_economic_index (ALC-1456). */
export const ECONOMIC_INDEX_SYNC_STATUSES = ['SUCCESS', 'FAILED'] as const;
export type EconomicIndexSyncStatus = (typeof ECONOMIC_INDEX_SYNC_STATUSES)[number];

/**
 * Stable SGS sync error codes (ALC-1456). Classification uses the shared
 * `@alinea/external-apis` error classes / optional HTTP status — never message substrings.
 */
export const SgsSyncErrorCode = {
  VALIDATION: 'VALIDATION',
  NETWORK: 'NETWORK',
  UPSTREAM_5XX: 'UPSTREAM_5XX',
  UPSTREAM_4XX: 'UPSTREAM_4XX',
  RATE_LIMIT: 'RATE_LIMIT',
  MALFORMED_PAYLOAD: 'MALFORMED_PAYLOAD',
  UNKNOWN: 'UNKNOWN',
} as const;
export type SgsSyncErrorCode = (typeof SgsSyncErrorCode)[keyof typeof SgsSyncErrorCode];

export interface EconomicIndexSyncHealthResponse {
  lastSyncAt: string | null;
  lastInsertedCount: number | null;
  lastStatus: EconomicIndexSyncStatus | null;
  lastErrorCode: SgsSyncErrorCode | string | null;
  lastErrorMessage: string | null;
}

export interface EconomicIndexFreshnessResponse {
  state: EconomicIndexFreshnessState;
  latestReferenceDate: string | null;
  requiredReferenceDate: string | null;
  lagCalendarDays: number | null;
  lagWeekdays: number | null;
  thresholdKind: 'WEEKDAYS' | 'MONTHLY_GRACE_DAYS';
  thresholdValue: number;
  reason: string;
}

export interface EconomicIndexResponse {
  code: string;
  name: string;
  frequency: string;
  compounding: string;
  sgsSeriesCode: number;
  active: boolean;
  latestObservation: { referenceDate: string; value: string } | null;
  syncHealth: EconomicIndexSyncHealthResponse;
  freshness: EconomicIndexFreshnessResponse;
}

export interface UsageTierRequest {
  rangeStart: number;
  rangeEnd?: number | null;
  unitPrice?: number | null;
  flatPrice?: number | null;
  floorPrice?: number | null;
}

export interface UsageTierResponse extends UsageTierRequest {
  tierId: string;
  sortOrder: number;
  effectiveFrom: string;
}

export interface ServiceSegmentRequest {
  segmentId: string;
  valueId: string;
}

export type ServiceSegmentResponse = ServiceSegmentRequest;

/** Percentage template classification for a billing service catalog item. */
export interface BillingServiceClassification {
  glAccounts: DimensionSplit[];
  costCenters?: DimensionSplit[];
  departments?: DimensionSplit[];
  managerialAccounts?: DimensionSplit[];
  segments?: SegmentSplit[];
  allocationType?: ApAllocationType;
  customBreakdown?: CustomBreakdown;
  notes?: string | null;
}

export interface BillingServiceClassificationInput {
  glAccounts: DimensionSplitInput[];
  costCenters?: DimensionSplitInput[];
  departments?: DimensionSplitInput[];
  managerialAccounts?: DimensionSplitInput[];
  segments?: SegmentSplitInput[];
  allocationType?: ApAllocationType;
  customBreakdown?: CustomBreakdownInput;
  notes?: string | null;
}

export interface BillingServiceResponse {
  serviceId: string;
  entityId: string;
  code: string;
  externalProductId?: string | null;
  fiscalServiceTypeId?: string | null;
  name: string;
  description?: string | null;
  billingMode: BillingMode;
  usageUnitName?: string | null;
  baseFee?: number | null;
  includedUnits?: number | null;
  minimumCommitment?: number | null;
  cap?: number | null;
  floorPrice?: number | null;
  defaultStandaloneSellingPrice?: number | null;
  calculationMode?: ServiceCalculationMode | null;
  /** Fee as a fraction of GTV when calculationMode is PERCENT_OF_GTV (0.0423 = 4.23%). */
  gtvRate?: number | null;
  defaultBillingTiming?: BillingTiming | null;
  defaultRecognitionMethod?: RevenueRecognitionMethod | null;
  currency: string;
  cnaeCode?: string | null;
  cnaeDescription?: string | null;
  serviceCode?: string | null;
  municipalServiceCode?: string | null;
  municipalServiceDescription?: string | null;
  irrfNature?: IrrfNature | null;
  issRate?: number | null;
  issMunicipalityIbgeCode?: string | null;
  issMunicipalityName?: string | null;
  issMunicipalityState?: string | null;
  issWithheldByDefault: boolean;
  nfseIssTaxation: NfseIssTaxation;
  /** Who issues this service's nota fiscal: Alchemy ERP, or an external system. */
  nfIssuer: BillingServiceNfIssuer;
  serviceResultCountryCode?: string | null;
  isLaborProvisionOverride?: boolean | null;
  nbsCode?: string | null;
  cstIbsCbs?: string | null;
  cClassTrib?: string | null;
  cIndOp?: string | null;
  ibsCbsBaseReductionPct?: number | null;
  /** @deprecated Use classification; removed after Phase 2 cutover. */
  glRevenueAccountId?: string | null;
  /** @deprecated Use classification; removed after Phase 2 cutover. */
  managerialAccountId?: string | null;
  /** @deprecated Use classification; removed after Phase 2 cutover. */
  costCenterId?: string | null;
  classification?: BillingServiceClassification | null;
  nfseDescriptionTemplate?: string | null;
  /** CFDI (México): SAT product/service key (ClaveProdServ) for emission. */
  claveProdServ?: string | null;
  /** CFDI (México): SAT unit key (ClaveUnidad), e.g. E48 for service. */
  satUnitCode?: string | null;
  usageTiers: UsageTierResponse[];
  /** @deprecated Use classification.segments; removed after Phase 2 cutover. */
  segments: ServiceSegmentResponse[];
  status: BillingServiceStatus;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateBillingServiceRequest {
  code?: string;
  externalProductId?: string | null;
  fiscalServiceTypeId?: string | null;
  entityId: string;
  name: string;
  description?: string | null;
  billingMode?: BillingMode;
  usageUnitName?: string | null;
  baseFee?: number | null;
  includedUnits?: number | null;
  minimumCommitment?: number | null;
  cap?: number | null;
  floorPrice?: number | null;
  defaultStandaloneSellingPrice?: number | null;
  calculationMode?: ServiceCalculationMode | null;
  /** Fee as a fraction of GTV when calculationMode is PERCENT_OF_GTV (0.0423 = 4.23%). */
  gtvRate?: number | null;
  defaultBillingTiming?: BillingTiming | null;
  defaultRecognitionMethod?: RevenueRecognitionMethod | null;
  currency?: string;
  cnaeCode?: string | null;
  cnaeDescription?: string | null;
  serviceCode?: string | null;
  municipalServiceCode?: string | null;
  municipalServiceDescription?: string | null;
  irrfNature?: IrrfNature | null;
  issRate?: number | null;
  issMunicipalityIbgeCode?: string | null;
  issMunicipalityName?: string | null;
  issMunicipalityState?: string | null;
  issWithheldByDefault?: boolean;
  nfseIssTaxation?: NfseIssTaxation;
  /** Default ALCHEMY. */
  nfIssuer?: BillingServiceNfIssuer;
  serviceResultCountryCode?: string | null;
  isLaborProvisionOverride?: boolean | null;
  nbsCode?: string | null;
  cstIbsCbs?: string | null;
  cClassTrib?: string | null;
  cIndOp?: string | null;
  ibsCbsBaseReductionPct?: number | null;
  /** @deprecated Use classification; removed after Phase 2 cutover. */
  glRevenueAccountId?: string | null;
  /** @deprecated Use classification; removed after Phase 2 cutover. */
  managerialAccountId?: string | null;
  /** @deprecated Use classification; removed after Phase 2 cutover. */
  costCenterId?: string | null;
  classification?: BillingServiceClassificationInput;
  nfseDescriptionTemplate?: string | null;
  /** CFDI (México): SAT product/service key (ClaveProdServ) for emission. */
  claveProdServ?: string | null;
  /** CFDI (México): SAT unit key (ClaveUnidad), e.g. E48 for service. */
  satUnitCode?: string | null;
  usageTiers?: UsageTierRequest[];
  /** @deprecated Use classification.segments; removed after Phase 2 cutover. */
  segments?: ServiceSegmentRequest[];
  status?: BillingServiceStatus;
  isActive?: boolean;
}

export type UpdateBillingServiceRequest = Partial<CreateBillingServiceRequest>;

export interface ContractTemplateItemRequest {
  serviceId: string;
  /** Whether this line bills as its own invoice or accumulates into the period's aggregate. */
  invoicing?: ServiceInvoicing;
  regularPrice?: number | null;
  introductoryPrice?: number | null;
  introductoryMonths?: number | null;
  includeForMonths?: number | null;
}

/** One average metered quantity supplied solely for a non-persistent potential simulation. */
export interface ContractPotentialUsageAssumption {
  serviceId: string;
  averageQuantity: number;
}

export interface ContractPotentialSimulationRequest {
  usageAssumptions?: ContractPotentialUsageAssumption[];
  /** Current non-persisted terms from an edit form, used only for this projection. */
  proposal?: ContractPotentialSimulationProposal;
}

export interface ContractPotentialSimulationProposal {
  entityId: string;
  currency: string;
  billingFrequency: BillingFrequency;
  durationMonths?: number | null;
  items: ContractTemplateItemRequest[];
}

export interface ContractPotentialSimulationMonth {
  /** 1-based month in the simulated contract term. */
  month: number;
  grossAmount: number;
  /** Revenue net of the issuer's own ISS, PIS, and COFINS; customer withholdings remain tax credits. */
  netAmount: number | null;
  ownIssAmount: number | null;
  withholdingAmount: number | null;
  deductions: ContractPotentialDeductions | null;
  services: ContractPotentialSimulationServiceBreakdown[];
}

export interface ContractPotentialDeductions {
  /** Issuer tax expense; not withheld by the customer. */
  ownIssAmount: number;
  issWithheldAmount: number;
  /** PIS/COFINS owed on the issuer's revenue, using the entity configuration. */
  pisAmount: number;
  cofinsAmount: number;
  /** Customer withholdings, shown separately from the issuer's own PIS/COFINS expense. */
  pisWithheldAmount: number;
  cofinsWithheldAmount: number;
  csllAmount: number;
  irrfAmount: number;
  inssAmount: number;
}

export interface ContractPotentialSimulationServiceBreakdown {
  serviceId: string;
  serviceCode: string;
  serviceName: string;
  grossAmount: number;
  netAmount: number | null;
  deductions: ContractPotentialDeductions | null;
}

export interface ContractPotentialSimulationResponse {
  currency: string;
  durationMonths: number;
  fiscalStatus: 'AVAILABLE' | 'UNAVAILABLE';
  missingFiscalParameters: string[];
  totalGrossAmount: number;
  totalNetAmount: number | null;
  totalOwnIssAmount: number | null;
  totalWithholdingAmount: number | null;
  totalDeductions: ContractPotentialDeductions | null;
  months: ContractPotentialSimulationMonth[];
}

export interface ContractTemplateItemResponse extends ContractTemplateItemRequest {
  templateItemId: string;
  sortOrder: number;
}

export interface ContractTemplateResponse {
  templateId: string;
  entityId: string;
  code: string;
  name: string;
  description?: string | null;
  billingFrequency: BillingFrequency;
  defaultDurationMonths?: number | null;
  currency: string;
  proRataFirstCycle: boolean;
  /** Where the billing occurrence is anchored in the month (C3). */
  billingDayAnchor: BillingDayAnchor;
  revenueRecognitionModel: 'ON_FISCAL_DOCUMENT' | 'ON_SETTLEMENT';
  settlementModel: 'DIRECT' | 'CUSTODIAL';
  autoApproveTransactions: boolean;
  /** Default revenue cycle of contracts built from this template. Null = the system Padrão cycle. */
  revenueCycleId: string | null;
  billingDayOfMonth?: number | null;
  autoRenewal: boolean;
  renewalDurationMonths?: number | null;
  renewalAdjustmentType?: RenewalAdjustmentType | null;
  renewalAdjustmentValue?: number | null;
  renewalIndex?: string | null;
  renewalAccumulatedVariationWindowMonths?: number | null;
  renewalPublicationLagDays?: number | null;
  renewalFloorAtZero: boolean;
  cancellationNoticeDays?: number | null;
  defaultBillingTiming?: BillingTiming | null;
  defaultServicePeriodRule?: ContractItemServicePeriodRule | null;
  defaultRecognitionMethod?: RevenueRecognitionMethod | null;
  paymentMethod?: PaymentMethod | null;
  /** Payment installments a generated invoice's total is split into (ALC-1199). Null/1 = single installment. */
  installmentCount?: number | null;
  /** Day-spacing between successive installment due dates. Only meaningful when installmentCount > 1. */
  installmentIntervalDays?: number | null;
  paymentTermsDays?: number | null;
  boletoInstructionsOverride?: string | null;
  items: ContractTemplateItemResponse[];
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateContractTemplateRequest {
  code?: string;
  entityId: string;
  name: string;
  description?: string | null;
  billingFrequency?: BillingFrequency;
  defaultDurationMonths?: number | null;
  currency?: string;
  proRataFirstCycle?: boolean;
  billingDayAnchor?: BillingDayAnchor;
  revenueRecognitionModel?: 'ON_FISCAL_DOCUMENT' | 'ON_SETTLEMENT';
  settlementModel?: 'DIRECT' | 'CUSTODIAL';
  autoApproveTransactions?: boolean;
  /** Null = the system Padrão cycle. */
  revenueCycleId?: string | null;
  billingDayOfMonth?: number | null;
  autoRenewal?: boolean;
  renewalDurationMonths?: number | null;
  renewalAdjustmentType?: RenewalAdjustmentType | null;
  renewalAdjustmentValue?: number | null;
  renewalIndex?: string | null;
  renewalAccumulatedVariationWindowMonths?: number | null;
  renewalPublicationLagDays?: number | null;
  renewalFloorAtZero?: boolean;
  cancellationNoticeDays?: number | null;
  defaultBillingTiming?: BillingTiming | null;
  defaultServicePeriodRule?: ContractItemServicePeriodRule | null;
  defaultRecognitionMethod?: RevenueRecognitionMethod | null;
  paymentMethod?: PaymentMethod | null;
  installmentCount?: number | null;
  installmentIntervalDays?: number | null;
  paymentTermsDays?: number | null;
  boletoInstructionsOverride?: string | null;
  items?: ContractTemplateItemRequest[];
  isActive?: boolean;
}

export type UpdateContractTemplateRequest = Partial<CreateContractTemplateRequest>;

export interface BillingGroupResponse {
  billingGroupId: string;
  code: string;
  name: string;
  description?: string | null;
  boletoInstructions?: string | null;
  negativacaoEnabled: boolean;
  negativacaoDays?: number | null;
  autoCollectionEnabled: boolean;
  communicationChannels: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateBillingGroupRequest {
  code?: string;
  name: string;
  description?: string | null;
  boletoInstructions?: string | null;
  negativacaoEnabled?: boolean;
  negativacaoDays?: number | null;
  autoCollectionEnabled?: boolean;
  communicationChannels?: string[];
  isActive?: boolean;
}

export type UpdateBillingGroupRequest = Partial<CreateBillingGroupRequest>;

export interface CustomerBalanceEntryResponse {
  entryId: string;
  customerId: string;
  type: CustomerBalanceType;
  reason: CustomerBalanceReason;
  currencyCode: string;
  amount: string;
  amountRemaining: string;
  description: string;
  referenceId?: string | null;
  status: CustomerBalanceStatus;
  expiresAt?: string | null;
  createdBy?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  cancelledBy?: string | null;
  cancelledAt?: string | null;
}

export interface CustomerBalanceCurrencySummaryResponse {
  currencyCode: string;
  totalCredits: string;
  totalDebits: string;
  netBalance: string;
}

export interface CustomerBalanceSummaryResponse {
  byCurrency: CustomerBalanceCurrencySummaryResponse[];
}

export interface CustomerBalanceListResponse {
  entries: CustomerBalanceEntryResponse[];
  summary: CustomerBalanceSummaryResponse;
}

export interface CreateCustomerBalanceEntryRequest {
  type: CustomerBalanceType;
  reason: CustomerBalanceReason;
  currencyCode: string;
  amount: string;
  description: string;
  referenceId?: string | null;
  expiresAt?: string | null;
  notes?: string | null;
}

export interface CustomerEmailResponse {
  customerEmailId: string;
  customerId: string;
  email: string;
  role: CustomerEmailRole;
  isPrimary: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateCustomerEmailRequest {
  email: string;
  role: CustomerEmailRole;
  isPrimary?: boolean;
  isActive?: boolean;
}

export type UpdateCustomerEmailRequest = Partial<CreateCustomerEmailRequest>;

/**
 * Full resolved BillingService pricing shape, frozen into a BillingContractItem at
 * version-creation time. Immutable once stored — later edits to the live
 * BillingService never change an already-activated Contract's effective pricing.
 *
 * Monetary/quantity fields are exact decimal strings, not `number` — unlike
 * `UsageTierRequest`/`Response` (the live, editable BillingService CRUD surface, where a
 * `number` round-trip is a display/edit-form concern only, backed by an exact Decimal
 * column regardless), this snapshot is write-once and is the sole input the pricing
 * evaluator (ALC-1198) uses to compute a real invoice charge — a lossy Decimal->number
 * round trip here would mean the actual billed amount can silently differ from the
 * contractual term (accounting/finance review ACC-01, ALC-1209). Never widen these back
 * to `number`.
 */
export interface ContractItemPricingSnapshotTier {
  tierId: string;
  sortOrder: number;
  rangeStart: string;
  rangeEnd?: string | null;
  unitPrice?: string | null;
  flatPrice?: string | null;
  floorPrice?: string | null;
  effectiveFrom: string;
}

export interface ContractItemPricingSnapshot {
  usageUnitName?: string | null;
  baseFee?: string | null;
  includedUnits?: string | null;
  minimumCommitment?: string | null;
  cap?: string | null;
  floorPrice?: string | null;
  calculationMode?: ServiceCalculationMode | null;
  /**
   * Frozen at version creation (activation / amendment approval). SaaS metrics and
   * other commercial classifiers must read this instead of the live BillingService
   * so a later catalog edit cannot rewrite historical MRR (ALC-1419 / Q-06).
   * Optional for pre-freeze rows; readers may fall back to the live service once.
   */
  billingMode?: BillingMode | null;
  defaultStandaloneSellingPrice?: string | null;
  defaultBillingTiming?: BillingTiming | null;
  defaultRecognitionMethod?: RevenueRecognitionMethod | null;
  revenueGlAccountId?: string | null;
  revenueClassification?: unknown;
  /** Immutable segment semantics captured with revenueClassification. */
  revenueSegmentPolicy?: { version: 1; isMeceBySegmentId: Record<string, boolean> };
  tiers: ContractItemPricingSnapshotTier[];
}

export interface ContractDraftItemRequest {
  serviceId: string;
  /** Whether this line bills as its own invoice or accumulates into the period's aggregate. */
  invoicing?: ServiceInvoicing;
  /** Regular item price after any introductory period. */
  regularPrice?: number;
  /**
   * @deprecated Use regularPrice. Retained for existing API consumers during the
   * timed-terms migration.
   */
  billablePrice?: number;
  introductoryPrice?: number | null;
  introductoryMonths?: number | null;
  includeForMonths?: number | null;
  standaloneSellingPrice?: number | null;
  billingTiming?: BillingTiming | null;
  servicePeriodRule?: ContractItemServicePeriodRule | null;
  recognitionMethod?: RevenueRecognitionMethod | null;
}

export interface ContractDraftItemResponse extends ContractDraftItemRequest {
  draftItemId: string;
  regularPrice: number;
  /** @deprecated Use regularPrice. */
  billablePrice: number;
  sortOrder: number;
}

export interface ContractItemResponse {
  itemId: string;
  serviceId: string;
  billablePrice: number;
  standaloneSellingPrice?: number | null;
  /** Consideration allocated to this performance obligation using relative SSP. */
  allocatedAmount: number;
  billingTiming?: BillingTiming | null;
  servicePeriodRule?: ContractItemServicePeriodRule | null;
  recognitionMethod?: RevenueRecognitionMethod | null;
  pricingSnapshot: ContractItemPricingSnapshot;
  sortOrder: number;
  /** Whether this line bills as its own invoice or accumulates into the period's aggregate. */
  invoicing?: ServiceInvoicing;
  /**
   * @deprecated The item decides on its own since the invoicing moved off the service
   * (review 12/09, D2). Kept as a mirror of `invoicing` so existing readers do not break.
   */
  serviceInvoicing?: ServiceInvoicing;
}

export interface ContractConsiderationAllocationItem {
  serviceId: string;
  sortOrder: number;
  billablePrice: string;
  standaloneSellingPrice: string | null;
  allocatedAmount: string;
}

/** Evidence supporting CPC 47 allocation of an introductory price to its own obligation. */
export interface ContractIntroductoryPriceAllocationException {
  type: 'ITEM_SPECIFIC_INTRODUCTORY_PRICE';
  criteria: string[];
  items: {
    serviceId: string;
    regularPrice: string;
    standaloneSellingPrice: string;
  }[];
}

/** Immutable CPC 47 allocation evidence captured when a contract Version is created. */
export interface ContractConsiderationAllocationSnapshot {
  method: 'RELATIVE_STANDALONE_SELLING_PRICE';
  transactionConsideration: string;
  totalStandaloneSellingPrice: string | null;
  bundledDiscount: boolean;
  /** Present only when introductory-price evidence supports item-specific allocation. */
  introductoryPriceException?: ContractIntroductoryPriceAllocationException;
  items: ContractConsiderationAllocationItem[];
}

export interface ContractVersionResponse {
  versionId: string;
  versionNumber: number;
  effectiveFrom: string;
  createdAt: string;
  allocationSnapshot: ContractConsiderationAllocationSnapshot;
  items: ContractItemResponse[];
}

export interface BillingContractResponse {
  contractId: string;
  entityId: string;
  customerId: string;
  responsibleUserId?: string | null;
  responsibleUserName?: string | null;
  responsibleUserEmail?: string | null;
  templateId?: string | null;
  templateName?: string | null;
  code: string;
  customerName?: string | null;
  customerCode?: string | null;
  currency: string;
  startDate?: string | null;
  status: BillingContractStatus;
  billingFrequency?: BillingFrequency;
  billingDayOfMonth?: number | null;
  proRataFirstCycle?: boolean;
  /** How the contract's revenue reaches the books and how its invoices are born. */
  revenueRecognitionModel?: 'ON_FISCAL_DOCUMENT' | 'ON_SETTLEMENT';
  settlementModel?: 'DIRECT' | 'CUSTODIAL';
  /** Transactions of this contract are approved on creation by rule. */
  autoApproveTransactions?: boolean;
  /** The contract's own revenue-cycle override. Null = the template's cycle, else Padrão. */
  revenueCycleId?: string | null;
  /** The contract's own revenue-cycle override with its name. Null = none (follows the template). */
  revenueCycle?: BillingContractRevenueCycleFields['revenueCycle'];
  /** The cycle the contract bills under: its own, else its template's, else Padrão. */
  effectiveRevenueCycle?: BillingContractRevenueCycleFields['effectiveRevenueCycle'];
  /** How the current version's items bill: all individual, all aggregated, or mixed. */
  itemInvoicing?: 'INDIVIDUAL' | 'PERIOD_AGGREGATE' | 'MIXED' | null;
  billingDayAnchor?: BillingDayAnchor;
  paymentMethod?: PaymentMethod | null;
  installmentCount?: number | null;
  installmentIntervalDays?: number | null;
  /** Net payment terms (days from issue date to due date). Null/0 = due on issue date. */
  paymentTermsDays?: number | null;
  /** ISO timestamp when status most recently became SUSPENDED; null otherwise (ALC-1419). */
  suspendedAt?: string | null;
  /**
   * Length of the term currently in force, in months. Snapshotted from the template's
   * `defaultDurationMonths` at creation and overridable per contract. Distinct from
   * `renewalDurationMonths`, which sizes the term the contract renews INTO.
   */
  termDurationMonths?: number | null;
  /** Start of the term in force. Equal to `startDate` until a renewal rolls it. */
  currentTermStartDate?: string | null;
  /** Last day the current term covers, inclusive. Null when the term is undetermined. */
  currentTermEndDate?: string | null;
  /** First day of the next term. Null unless the contract auto-renews. */
  nextRenewalDate?: string | null;
  /**
   * True when the contract has no determinable term — no start date, or no term duration
   * to snapshot (no template, or a template without a default duration). Such contracts
   * must be shown as needing a term, never as open-ended.
   */
  termUndetermined: boolean;
  autoRenewal: boolean;
  renewalDurationMonths?: number | null;
  renewalAdjustmentType?: RenewalAdjustmentType | null;
  renewalAdjustmentValue?: number | null;
  renewalIndex?: string | null;
  renewalAccumulatedVariationWindowMonths?: number | null;
  renewalPublicationLagDays?: number | null;
  renewalFloorAtZero: boolean;
  cancellationNoticeDays?: number | null;
  draftItems: ContractDraftItemResponse[];
  /** The version effective as of "now", present only once the contract has activated. */
  currentVersion?: ContractVersionResponse | null;
  /** Full version history, returned on detail responses. List summaries may omit it. */
  versions?: ContractVersionResponse[];
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateBillingContractRequest {
  code?: string;
  /**
   * The integrator's own subscription identifier, set only by the External API
   * (ALC-2235). Part of the create request rather than a follow-up update so the contract
   * and its reference are inserted atomically — a long reference has no usable `code` to
   * fall back on, so a contract committed without it is unreachable by every by-reference
   * route.
   */
  externalReferenceId?: string;
  entityId: string;
  customerId: string;
  responsibleUserId?: string | null;
  templateId?: string | null;
  currency?: string;
  startDate?: string | null;
  /**
   * Initial contractual term in months. Defaults from the template's
   * `defaultDurationMonths` when omitted; required before the contract can activate.
   */
  termDurationMonths?: number | null;
  autoRenewal?: boolean;
  renewalDurationMonths?: number | null;
  renewalAdjustmentType?: RenewalAdjustmentType | null;
  renewalAdjustmentValue?: number | null;
  renewalIndex?: string | null;
  renewalAccumulatedVariationWindowMonths?: number | null;
  renewalPublicationLagDays?: number | null;
  renewalFloorAtZero?: boolean;
  cancellationNoticeDays?: number | null;
  /** The contract's own revenue-cycle override. Omitted or null = the template's cycle, else Padrão. */
  revenueCycleId?: string | null;
  /** Overrides the template's paymentTermsDays for this contract (defaults from the
   * template when omitted). Null/0 = due on issue date. */
  paymentTermsDays?: number | null;
  /** Overrides the template's billing cadence for this contract (ALC-2234). Defaults
   * from the template, then to MONTHLY. */
  billingFrequency?: BillingFrequency;
  billingDayOfMonth?: number | null;
  billingDayAnchor?: BillingDayAnchor;
  paymentMethod?: PaymentMethod | null;
  installmentCount?: number | null;
  installmentIntervalDays?: number | null;
  /** Historical-import cutover (see BillingContract.automationStartDate). Null keeps the
   * legacy behavior of generating from startDate. */
  automationStartDate?: string | null;
  draftItems?: ContractDraftItemRequest[];
  /**
   * Pass-through integrator metadata (ALC-2236), set only by the External API. Part of
   * the create request rather than a follow-up update, for the same atomicity reason as
   * `externalReferenceId` above: a crash between insert and a follow-up update would
   * strand the contract without the metadata a concurrent replay believes it already
   * wrote. Not settable from the Platform UI. Never `null` — the External API rejects
   * `metadata: null` with `METADATA_INVALID` before this type is ever constructed, so
   * `undefined` (omitted) is the only "no metadata" value that reaches here.
   */
  metadata?: Record<string, string>;
}

export type UpdateBillingContractRequest = Partial<Omit<CreateBillingContractRequest, 'entityId'>>;

/**
 * Narrow renewal-settings edit on a contract that is already ACTIVE (ALC-2234).
 *
 * Deliberately NOT a general ACTIVE edit: `update()` stays DRAFT-only. Only the
 * fields that describe what happens at the NEXT renewal are mutable here, because
 * they change no already-committed Version, price, or posted revenue — they only
 * change the forward projection. Turning auto-renewal off is exactly the signal
 * that keeps the revenue forecast honest, and it must not require cancelling and
 * re-creating the contract.
 */
export interface UpdateContractRenewalSettingsRequest {
  autoRenewal: boolean;
  renewalDurationMonths?: number | null;
}

/**
 * Fast-path creation: customer + template + start date only. The service resolves
 * everything else (entity, currency, draft items, renewal terms) from the template
 * and its services as an immutable snapshot — later edits to the template or its
 * services never change an already-created draft or contract.
 */
export interface CreateBillingContractFromTemplateRequest {
  customerId: string;
  templateId: string;
  startDate: string;
  /**
   * Initial contractual term in months. Defaults to the template's
   * `defaultDurationMonths`; supply it when the template carries no default.
   */
  termDurationMonths?: number | null;
  /**
   * External-API-only fields (ALC-2820), mirroring `CreateBillingContractRequest`'s own
   * `code`/`externalReferenceId`/`metadata`/`automationStartDate` — written into the SAME
   * insert as the contract for the same atomicity reason documented there: a crash
   * between this insert and a follow-up update would strand the contract without a
   * reference every by-reference route needs, or without metadata a concurrent replay
   * believes is already stored. Not settable from the Platform UI.
   */
  code?: string;
  externalReferenceId?: string;
  metadata?: Record<string, string>;
  automationStartDate?: string | null;
  /**
   * Per-service PRICE overrides layered on top of the template's copied-down item list
   * (ALC-2820) — "price is per person": the SAME template can back contracts billing
   * different people different amounts. Every entry must name a service the template
   * actually carries; `regularPrice` is the only overridable field — introductory terms,
   * billing timing, recognition method and every other per-item policy stay exactly what
   * the template (and its services' own defaults) already resolved.
   */
  itemPriceOverrides?: { serviceId: string; regularPrice: number }[];
}

export interface ContractStatusTransitionRequest {
  status: BillingContractStatus;
  /** Reverse posted usage-asset revenue that no issued invoice has reclassified. */
  reverseUnissuedRevenue?: boolean;
  /** Audit reason applied to every automatic revenue reversal. */
  revenueReversalReason?: string;
  /** Requested GL date for the reversals (`YYYY-MM-DD`); closed periods clamp forward. */
  revenueReversalDate?: string;
}

export interface ActivateContractRequest {
  confirmBackdatedActivation?: boolean;
}

export interface ContractBackdatedActivationPreviewBillingPeriod {
  /** Billing date that would already have been due. */
  billingDate: string;
  periodStartDate: string;
  periodEndDate: string;
}

export interface ContractBackdatedActivationPreviewPeriod {
  /** Competency month that would already need recognition attention. */
  periodDate: string;
  periodStartDate: string;
  periodEndDate: string;
}

/**
 * A missed-cycle preview when a Contract activates with a startDate in the past.
 * Phase 2 (billing execution) owns actually generating the missed cycles; this is
 * only an extension point so activation surfaces the gap instead of hiding it.
 */
export interface ContractBackdatedActivationPreview {
  startDate: string;
  asOfDate: string;
  missedBillingPeriods: ContractBackdatedActivationPreviewBillingPeriod[];
  missedRecognitionPeriods: ContractBackdatedActivationPreviewPeriod[];
}

export interface ActivateContractResponse {
  contract: BillingContractResponse;
  backdatedPreview: ContractBackdatedActivationPreview | null;
}

/** Frozen observation copy stored on a renewal calculation snapshot (ALC-1420). */
export interface RenewalAdjustmentObservationSnapshot {
  referenceDate: string;
  value: string;
  source: string;
  sgsSeriesCode: number;
  fetchedAt: string;
}

/**
 * Persisted renewal price-adjustment calculation (ALC-1420). Reproducible from
 * `observationsSnapshot` + `formula` alone — the live economic-index series is not required.
 */
export interface RenewalAdjustmentCalculationResponse {
  calculationId: string;
  contractId: string;
  asOfDate: string;
  status: RenewalCalculationStatus;
  adjustmentType: RenewalAdjustmentType;
  indexCode?: string | null;
  windowMonths?: number | null;
  publicationLagDays?: number | null;
  floorAtZero: boolean;
  accumulatedVariationPercent?: string | null;
  appliedAdjustmentPercent?: string | null;
  floorApplied: boolean;
  blockReason?: string | null;
  formula: string;
  formulaVersion: string;
  observationsSnapshot: RenewalAdjustmentObservationSnapshot[];
  createdAt: string;
}

export interface BillingContractListResponse extends PaginatedResponse<BillingContractResponse> {
  statusCounts: Record<BillingContractStatus | 'all', number>;
}

export interface ContractAmendmentItemRequest {
  serviceId: string;
  billablePrice: number;
  standaloneSellingPrice?: number | null;
  billingTiming?: BillingTiming | null;
  servicePeriodRule?: ContractItemServicePeriodRule | null;
  recognitionMethod?: RevenueRecognitionMethod | null;
}

export interface ContractAmendmentItemResponse extends ContractAmendmentItemRequest {
  amendmentItemId: string;
  sortOrder: number;
}

/**
 * One line of the before/after diff between an amendment's proposed items and its
 * baseVersion's items, matched by serviceId. `before` is null for a service the
 * amendment adds; `after` is null for a service the amendment drops.
 */
export interface ContractAmendmentDiffLine {
  serviceId: string;
  before: ContractItemResponse | null;
  after: ContractAmendmentItemResponse | null;
}

/**
 * Versioned change-control record for upsell/downgrade/suspension/cancellation/
 * renewal intent against a Contract's current terms. Approval creates a new, later
 * BillingContractVersion; it never mutates baseVersion or Contract.status —
 * the billing/recognition-side effects of the policy are a later phase's scope.
 * RENEWAL events are system-executed (ALC-1423) and carry `calculationId` +
 * `renewalTermStartDate` for audit/idempotency.
 */
export interface BillingContractAmendmentResponse {
  amendmentId: string;
  contractId: string;
  baseVersionId: string;
  amendmentType: BillingContractAmendmentType;
  reason: string;
  effectiveDate: string;
  status: BillingContractAmendmentStatus;
  createdBy: string;
  createdAt: string;
  decidedBy?: string | null;
  decidedAt?: string | null;
  resultingVersionId?: string | null;
  /** Present on RENEWAL events — links the reproducible adjustment snapshot (ALC-1423). */
  calculationId?: string | null;
  /** Present on RENEWAL events — term-start date used for DB-level idempotency (ALC-1423). */
  renewalTermStartDate?: string | null;
  items: ContractAmendmentItemResponse[];
  diff: ContractAmendmentDiffLine[];
}

/** Outcome of one contract in the daily renewal/expiry sweep (ALC-1423). */
export type ContractRenewalExpiryAction =
  | 'RENEWED'
  | 'HELD'
  | 'EXPIRED'
  | 'SKIPPED_ALREADY_RENEWED'
  | 'SKIPPED_ALREADY_EXPIRED'
  | 'SKIPPED_NO_TERM'
  | 'FAILED';

export interface ContractRenewalExpiryItemResult {
  contractId: string;
  action: ContractRenewalExpiryAction;
  /** Set when action is RENEWED or HELD. */
  calculationId?: string | null;
  amendmentId?: string | null;
  resultingVersionId?: string | null;
  blockReason?: string | null;
  errorMessage?: string | null;
}

export interface ContractRenewalExpiryRunReport {
  asOfDate: string;
  renewed: number;
  held: number;
  expired: number;
  skipped: number;
  failed: number;
  items: ContractRenewalExpiryItemResult[];
}

export interface CreateBillingContractAmendmentRequest {
  amendmentType: BillingContractAmendmentType;
  reason: string;
  effectiveDate: string;
  items: ContractAmendmentItemRequest[];
  /** Required when effectiveDate is backdated (in the past relative to today, date-only) —
   * mirrors ActivateContractRequest.confirmBackdatedActivation (ALC-1370 Item 2). */
  confirmBackdatedEffectiveDate?: boolean;
}

export interface ApproveBillingContractAmendmentRequest {
  /** Re-confirms a backdated effectiveDate at approval time — time can pass between
   * proposal and approval, so an effectiveDate that was future when created can have
   * become backdated by the time it is approved (ALC-1370 Item 2). */
  confirmBackdatedEffectiveDate?: boolean;
}

/**
 * Audited sign-off unblocking a below-floor commercial price at activation or amendment
 * approval (ALC-1189). Single-step — the act of creating this record IS the approval.
 * `amendmentId` is null for an activation-path exception, set for an amendment-path one.
 */
export interface BillingContractPricingExceptionResponse {
  exceptionId: string;
  contractId: string;
  amendmentId?: string | null;
  serviceId: string;
  requestedPrice: number;
  floorPrice: number;
  reason: string;
  approvedBy: string;
  approvedAt: string;
}

export interface CreateBillingContractPricingExceptionRequest {
  serviceId: string;
  /** Omit for an activation-path exception; set to a PENDING amendment's id for an amendment-path one. */
  amendmentId?: string | null;
  reason: string;
}

/**
 * One Contract Billing Cycle (or, since ALC-1199, one billed Contract Milestone) + its
 * invoice, generated by ContractBillingCycleService.runDueCycles (ALC-1196). `status` is
 * BLOCKED when the contract has an ARREARS item this generation could not bill yet
 * (ALC-1197) — see `blockedReason` for why, sourced from the invoice's INVOICE_BLOCKED
 * history event. A milestone entry has no `cycleId` (has `milestoneId` instead) — it is
 * never blocked, and never anchored to a ContractBillingCycle. Since ALC-1199,
 * compatible cycles/milestones may be consolidated: several entries can legitimately
 * share the same `invoiceId`.
 *
 * `invoiceId` is absent ONLY for a milestone that could not be billed because its
 * revenue/GL classification is unresolvable (no `itemId`, or an `itemId` that no
 * longer resolves — e.g. a legacy row written before that requirement existed).
 * Refusing to bill unclassified revenue must never mean silently vanishing from AR/cash
 * forecast: this entry (`status: 'BLOCKED'`, `blockedReason` set) surfaces the amount
 * every "Sync now"/cron pass until the milestone is corrected, exactly like a cycle
 * BLOCKED on PENDING_USAGE stays visible rather than disappearing (accounting/finance
 * review finding CFO-02, ALC-1199 follow-up).
 */
export interface GeneratedContractBillingCycleResponse {
  contractId: string;
  /** Present for a cycle-derived entry; absent for a billed-milestone entry. */
  cycleId?: string;
  /** Present for a billed-milestone entry (ALC-1199); absent for a cycle-derived entry. */
  milestoneId?: string;
  /** Absent only for an unresolvable-classification milestone — see interface doc. */
  invoiceId?: string;
  status: ContractBillingCycleStatus;
  blockedReason?: string | null;
  /** True when this pass detected that a previously-generated DRAFT invoice's source data
   * changed and moved it to STALE without overwriting the reviewed values (ALC-1201). The
   * cycle `status` itself stays GENERATED — staleness lives on the invoice, not the cycle. */
  staled?: boolean;
  /** Count of unresolved differences recorded on the STALE invoice — present only when
   * `staled` is true. */
  staleDifferenceCount?: number;
}

/**
 * Result of a "Sync now" (or 04:00 cron) contract billing cycle generation run.
 * `generated` only lists cycles newly created by *this* call — an occurrence already
 * generated by an earlier run is silently skipped (idempotent), not re-reported here.
 */
export interface SyncContractBillingCyclesResponse {
  contractsEvaluated: number;
  generated: GeneratedContractBillingCycleResponse[];
  /**
   * The other half of the same pre-billing pass: contracts whose items accumulate
   * into the period's aggregate instead of billing one by one. Reported here because
   * pre-billing is one job — a separate answer meant "did billing run?" had two.
   */
  aggregated?: {
    asOf: string;
    contracts: {
      contractId: string;
      entityId: string;
      customerId: string;
      currency: string;
      /** Null when the contract's first occurrence has not closed yet. */
      window: { periodStart: string; periodEnd: string } | null;
      created: number;
      refreshed: number;
      skippedIssued: number;
      aggregates: number;
      /**
       * Groups (or the whole window) whose aggregate could not be cut this run; the
       * rest of the run went on. Absent from runs that predate the field.
       */
      failed?: number;
      /** Why, one entry per failed group or window. */
      errors?: string[];
    }[];
  };
}

/**
 * One recorded difference on a STALE pre-billing invoice (ALC-1201): what the reviewed
 * draft currently holds (`previousValue`) versus what the billing engine would now produce
 * (`proposedValue`) for one contract item, after a source change. The reviewer must APPLY
 * (accept the proposed value) or DEFER (keep the reviewed value, with a reason) each one
 * before the invoice can return to DRAFT and become approvable again. Values are opaque
 * line snapshots (amount/quantity/description) for display — `null` on the side that does
 * not exist (`previousValue` for an added line, `proposedValue` for a removed one).
 */
export interface ContractBillingInvoiceDiffResponse {
  diffId: string;
  invoiceId: string;
  cycleId: string;
  contractItemId: string;
  diffType: ContractBillingInvoiceDiffType;
  label: string;
  previousValue: Record<string, unknown> | null;
  proposedValue: Record<string, unknown> | null;
  decision: ContractBillingInvoiceDiffDecision;
  decisionReason: string | null;
  decidedAt: string | null;
  /** Actor whose usage finalize caused this difference (ALC-1245 SoD). Null when unknown. */
  causedByUserId: string | null;
  /** True when APPLY/DEFER used the audited self-approval override (ALC-1245). */
  selfApprovalOverridden: boolean;
}

/**
 * The review state of a pre-billing invoice's stale differences (ALC-1201) — the current
 * invoice status plus every open difference. When `invoiceStatus` is no longer `STALE`
 * (every difference was applied or deferred), `differences` is empty and the invoice is
 * DRAFT (approvable) again.
 */
export interface ContractBillingInvoiceReviewResponse {
  invoiceId: string;
  invoiceStatus: ArInvoiceStatus;
  differences: ContractBillingInvoiceDiffResponse[];
}

/** Body for deferring one stale difference — the explicit reason is required (ALC-1201). */
export interface DeferContractBillingInvoiceDiffRequest {
  reason: string;
  /**
   * Audited self-approval override (ALC-1245). When the decider is the same actor who
   * caused the difference, APPLY/DEFER is blocked unless this is true AND the caller holds
   * `ar:contract:approve` (independent approval authority from `ar:contract:manage`).
   */
  overrideSelfApproval?: boolean;
}

/**
 * Optional body for applying one stale difference (ALC-1245). Empty body is the normal
 * path; the override fields are required together when the decider caused the difference.
 */
export interface ApplyContractBillingInvoiceDiffRequest {
  overrideSelfApproval?: boolean;
  /** Required when overrideSelfApproval is true — audited justification for the override. */
  overrideReason?: string;
}

/**
 * A milestone against a Contract (ALC-1199, recognition added in ALC-1257). `itemId` is null
 * for a contract-level milestone not tied to any one service/item. Once READY_TO_BILL, the
 * next "Sync now"/cron pass bills it into the same consolidation flow as due cycles
 * (see ContractBillingCycleService) and flips it to BILLED.
 *
 * Billing and recognition are configured independently: `bills` + `amount` drive invoicing,
 * `recognitionAmount` drives revenue. All four combinations are valid — bill only, recognize
 * only, both, or neither (a pure tracking marker).
 */
export interface ContractMilestoneResponse {
  milestoneId: string;
  contractId: string;
  itemId?: string | null;
  name: string;
  amount: number;
  /** Whether completing this milestone bills. False makes it recognition-only or tracking-only. */
  bills: boolean;
  /** Share of the item's allocated consideration this milestone earns; null = does not recognize. */
  recognitionAmount?: number | null;
  /** When completion evidence was recorded (denormalized from ContractRecognitionEvidence). */
  recognizedAt?: string | null;
  /** User who recorded the completion evidence — requires `gl:revenue-recognition:manage`. */
  recognizedBy?: string | null;
  status: ContractMilestoneStatus;
  readyToBillAt?: string | null;
  /** User who marked this milestone ready to bill (audit trail — ALC-1199). */
  readyToBillBy?: string | null;
  billedAt?: string | null;
  billedInvoiceId?: string | null;
  /** Set when `status` transitions to BLOCKED — why runDueCycles refused to bill this
   * milestone (unresolvable itemId). Deliberately never cleared by a later item
   * correction — it's the historical record of the original block, not a live
   * "currently blocked" flag (that's `status`). See ContractBillingCycleService. */
  blockedReason?: string | null;
  /** itemId this milestone had immediately before its most recent correction via
   * PATCH .../item. Paired with itemCorrectedBy/itemCorrectedAt (ALC-1199 follow-up). */
  previousItemId?: string | null;
  itemCorrectedAt?: string | null;
  /** User who corrected this milestone's item (audit trail — ALC-1199 follow-up). */
  itemCorrectedBy?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt?: string | null;
  createdBy?: string | null;
}

export interface CreateContractMilestoneRequest {
  name: string;
  /** Billing allocation. Must be 0 when `bills` is false. */
  amount: number;
  itemId?: string | null;
  /** Defaults to true (bill on completion), preserving the ALC-1199 billing-only behaviour. */
  bills?: boolean;
  /** Recognition allocation, independent of `amount`. Omit for a milestone that never earns. */
  recognitionAmount?: number | null;
}

/**
 * Corrects the item on a BLOCKED (or still-PENDING) milestone — the only way to
 * recover one `runDueCycles` blocked for an unresolvable classification back into the
 * billing pipeline (moves it to PENDING; re-authorizing to READY_TO_BILL is a
 * separate, explicit call to the existing mark-ready endpoint). See
 * ContractMilestoneService.updateItem.
 */
export interface UpdateContractMilestoneItemRequest {
  itemId: string;
}

/**
 * Corrects a milestone's billing/recognition allocations before it earns anything (ALC-1257) —
 * the way out of an item whose milestone allocations do not yet total its allocated
 * consideration, which the exactness gate otherwise refuses to recognize forever.
 */
export interface UpdateContractMilestoneAllocationsRequest {
  amount?: number;
  recognitionAmount?: number | null;
}

/**
 * Raw usage ingested for one ARREARS-billed Contract Item's service period (ALC-1197).
 * Only a FINALIZED row is visible to billing — see UsageRecordService.
 */
export interface UsageRecordResponse {
  usageId: string;
  contractId: string;
  versionId: string;
  contractItemId: string;
  periodStart: string;
  periodEnd: string;
  quantity: number;
  source: UsageRecordSource;
  externalId?: string | null;
  status: UsageRecordStatus;
  finalizedAt?: string | null;
  finalizedBy?: string | null;
  supersedesUsageId?: string | null;
  supersededAt?: string | null;
  ingestedAt: string;
  ingestedBy?: string | null;
}

export interface IngestUsageRecordRequest {
  contractItemId: string;
  periodStart: string;
  periodEnd: string;
  quantity: number;
  /** Provenance self-declared by the caller — defaults to MANUAL. The same authenticated
   * endpoint serves both human (MANUAL) and programmatic (API) callers; only the CSV
   * import endpoint forces CSV regardless of this field. */
  source?: UsageRecordSource;
  externalId?: string | null;
  rawPayload?: unknown;
}

/** Row-level outcome of one CSV usage import (ALC-1197). */
export interface UsageRecordCsvImportError {
  row: number;
  message: string;
}

export interface ImportUsageRecordsCsvResponse {
  imported: number;
  failed: number;
  errors: UsageRecordCsvImportError[];
}

/**
 * One competency-period row of a contract revenue-recognition schedule (ALC-1251).
 * Monetary amounts are `.toFixed(2)` strings; dates are `YYYY-MM-DD`.
 */
/**
 * One GL journal backing a recognition entry (ALC-1256 audit trail). The original posting
 * carries `isReversal: false` and flips to `reversed: true` once estornado; the counter-journal
 * is appended as `isReversal: true` for the same book.
 */
export interface ContractRevenueScheduleEntryPostingResponse {
  postingId: string;
  bookId: string;
  journalId: string;
  reversed: boolean;
  isReversal: boolean;
}

export interface ContractRevenueScheduleEntryResponse {
  entryId: string;
  /** The competency month this amount is recognized in (period anchor). */
  periodDate: string;
  periodStartDate: string;
  periodEndDate: string;
  amount: string;
  /** Functional-currency amount actually posted for a foreign-currency recognition. */
  functionalAmount?: string | null;
  /** Functional currency of the entity book used by the recognition posting. */
  functionalCurrency?: string | null;
  /** Immutable quote consumed by the recognition posting, when conversion was required. */
  exchangeRate?: string | null;
  exchangeRateSource?: string | null;
  exchangeRateRequestedDate?: string | null;
  exchangeRateActualDate?: string | null;
  status: string;
  /**
   * GL date this entry posts on when it differs from its competency — set on a correction
   * whose competency period had already closed (ALC-1256). Null in the normal case.
   */
  glPostingDate?: string | null;
  /** When the entry was reversed, and why (ALC-1256). */
  reversedAt?: string | null;
  reversalReason?: string | null;
  /** Set on a correction entry: the REVERSED entry it replaces. */
  correctsEntryId?: string | null;
  /** Who authorized the correction, and why — durable attribution for a manual revenue change. */
  correctedByUserId?: string | null;
  correctionReason?: string | null;
  /** Set on a REVERSED entry once corrected: the correction that replaced it. */
  correctedByEntryId?: string | null;
  /** Journal trace, original and reversal, so the audit walk is navigable from the UI. */
  journalPostings?: ContractRevenueScheduleEntryPostingResponse[];
  isCatchup: boolean;
  catchupAccumulatedAmount: string | null;
  catchupTargetEntryId: string | null;
  /**
   * ISS provisioned by this entry's recognition (own-ISS basis frozen at schedule creation,
   * ALC-1259) — `.toFixed(2)` string, `"0.00"` when no ISS applies. Decremented on a
   * recognition reversal by the same amount it added.
   */
  issProvisionedAmount: string;
  /**
   * Terminal-adjustment audit (ALC-1339) — set on a catch-up target that a CANCELLED/EXPIRED
   * transition trimmed to the already-earned closed-period history it aggregates and then
   * recognized. `terminalAdjustmentDiscardedAmount` is the target's own not-yet-earned
   * competency portion that was dropped, so `amount + discarded` reconciles to what the
   * schedule had planned for the row. Null on every other entry.
   */
  terminalAdjustmentAt?: string | null;
  terminalAdjustmentByUserId?: string | null;
  terminalAdjustmentReason?: string | null;
  terminalAdjustmentDiscardedAmount?: string | null;
}

/**
 * A contract's CPC 47 revenue-recognition schedule for one performance obligation
 * (contract item), over the current service window (ALC-1251 foundation).
 */
export interface ContractRevenueScheduleResponse {
  scheduleId: string;
  contractId: string;
  versionId: string;
  itemId: string;
  /** Set when this schedule belongs to a single milestone obligation (ALC-1257). */
  milestoneId?: string | null;
  recognitionMethod: RevenueRecognitionMethod;
  direction?: 'ADVANCE_LIABILITY' | 'USAGE_ASSET';
  /** Amount already reclassified into Trade AR by issued invoices. */
  reclassifiedAmount?: string;
  /** ISS provision already settled by an issued invoice. */
  issSettledAmount?: string;
  /**
   * Whether the schedule has frozen its revenue account, the precondition for recognizing any
   * entry. An ADVANCE_LIABILITY schedule freezes it when its invoice posts to the GL; a
   * USAGE_ASSET one when the usage is finalized. `false` means "awaiting the invoice/usage":
   * recognition would fail with CONTRACT_REVENUE_NO_SNAPSHOT.
   */
  revenueAccountSnapshotted?: boolean;
  totalAmount: string;
  currency: string;
  serviceStartDate: string;
  serviceEndDate: string;
  status: string;
  entries: ContractRevenueScheduleEntryResponse[];
}

export interface RunContractRevenueRecognitionRequest {
  /** Competency month whose due schedule entries should be recognized (`YYYY-MM`). */
  asOfMonth: string;
}

export interface ContractRevenueRecognitionBatchResponse {
  totalCount: number;
  successCount: number;
  failureCount: number;
  results: {
    entryId: string;
    scheduleId: string;
    contractId: string;
    status: 'POSTED' | 'SKIPPED' | 'FAILED';
    errorCode?: string;
    errorMessage?: string;
  }[];
  /**
   * D22 schedule (ALC-4473): the fixed Cost Service months ("Distribuir em N meses") the batch
   * posted as due by `asOfMonth`, one transaction per contract slot. Not counted in the totals
   * above, which stay revenue schedule entries.
   */
  costSchedule?: {
    posted: number;
    stillPending: number;
    failures: { costServiceId: string; contractId: string; bookId: string; errorCode: string }[];
  };
}

export interface RecognizeContractRevenueEntryResponse {
  journalIds: string[];
}

export interface ReverseContractRevenueEntryRequest {
  reason?: string;
  /** Requested GL date for the reversal (`YYYY-MM-DD`). */
  reversalDate?: string;
}

export interface ReverseContractRevenueEntryResponse {
  entryId: string;
  /** GL date the counter-journals landed on, forward-clamped when the original period closed. */
  reversalDate: string;
  reversalJournalIds: string[];
}

export interface CorrectContractRevenueEntryRequest {
  /** Corrected amount as a decimal string with at most 2 decimal places. */
  amount: string;
  reason?: string;
}

export interface CorrectContractRevenueEntryResponse {
  /** The REVERSED entry this correction replaces. */
  correctsEntryId: string;
  /** The new PENDING entry, ready to be recognized. */
  entryId: string;
  amount: string;
  /** Competency the correction belongs to (`YYYY-MM-DD`). */
  periodDate: string;
  /** GL date the correction will post on (`YYYY-MM-DD`). */
  glPostingDate: string;
}

/**
 * Authorized completion evidence for an event-recognized performance obligation (ALC-1257).
 * Append-only: a mistake is corrected by reversing the recognition it produced and recording
 * fresh evidence, never by editing the record of a revenue event.
 */
export interface ContractRecognitionEvidenceResponse {
  evidenceId: string;
  contractId: string;
  versionId: string;
  itemId: string;
  milestoneId?: string | null;
  kind: ContractRecognitionEvidenceKind;
  /** EFFECTIVE delivery/completion date (`YYYY-MM-DD`) — determines the competency month. */
  evidenceDate: string;
  /** Verifiable external proof: acceptance certificate, signed report, ticket. */
  reference: string;
  notes?: string | null;
  /** The recognition schedule this evidence created; null when the event earns nothing. */
  scheduleId?: string | null;
  recordedBy: string;
  recordedAt: string;
}

export interface RecordDeliveryEvidenceRequest {
  /** EFFECTIVE delivery/acceptance date (`YYYY-MM-DD`). May be backdated, never future. */
  evidenceDate: string;
  reference: string;
  notes?: string | null;
}

export interface RecordMilestoneCompletionRequest {
  /** EFFECTIVE completion date (`YYYY-MM-DD`). May be backdated, never future. */
  evidenceDate: string;
  reference: string;
  notes?: string | null;
}

/**
 * CPC 47 ¶105-109 (IFRS 15 / ASC 606-10-45) single-figure classification of a contract's
 * NET Contract Asset / Contract Liability position (ALC-1367). Gross per-contract GL
 * postings to the AR_CONTRACT_ASSET / AR_CONTRACT_LIABILITY children stay unnetted (drill-down
 * audit trail, ALC-1251); this is the presentation-layer rollup of the two.
 */
export type ContractNetPositionClassification =
  'NET_CONTRACT_ASSET' | 'NET_CONTRACT_LIABILITY' | 'SETTLED';

export interface ContractNetPositionResponse {
  contractId: string;
  entityId: string;
  /** `YYYY-MM-DD` the position was computed as of. */
  asOf: string;
  /** Contract Asset child's closing balance (natural/debit-signed; 0 if never generated). */
  contractAssetBalance: string;
  /** Contract Liability child's closing balance (natural/credit-signed; 0 if never generated). */
  contractLiabilityBalance: string;
  /**
   * `contractAssetBalance - contractLiabilityBalance`. Positive = net asset, negative = net
   * liability, ~0 = settled — mirrors `BalanceRowResponse.netBalance`'s signed convention.
   */
  netAmount: string;
  classification: ContractNetPositionClassification;
}

export interface ContractNetPositionListItem extends ContractNetPositionResponse {
  contractCode: string;
}

export interface ContractNetPositionListResponse {
  /** `YYYY-MM-DD` the position was computed as of. */
  asOf: string;
  /** Present only when the listing was filtered to one entity. */
  entityId?: string;
  /** Only contracts with a nonzero Contract Asset or Contract Liability balance. */
  items: ContractNetPositionListItem[];
  totals: {
    /** Sum of `netAmount` across NET_CONTRACT_ASSET contracts (positive magnitude). */
    netContractAssetTotal: string;
    /** Sum of |`netAmount`| across NET_CONTRACT_LIABILITY contracts (positive magnitude). */
    netContractLiabilityTotal: string;
  };
}

/**
 * One fixed-recurring contract item contributing to committed or suspended MRR (ALC-1419).
 * Traceability: every figure on the SaaS metrics endpoint can be drilled back to these rows.
 */
export interface BillingSaasMetricsSource {
  contractId: string;
  contractCode: string;
  entityId: string;
  customerId: string;
  versionId: string;
  itemId: string;
  serviceId: string;
  /** ACTIVE contributes to committed; SUSPENDED only to the suspended bucket. */
  status: 'ACTIVE' | 'SUSPENDED';
  billingFrequency: BillingFrequency;
  currency: string;
  /**
   * Per-cycle committed amount (billablePrice or metered baseFee).
   * `"0.00"` when the source exists only for forecast usage (pure metered, no baseFee).
   */
  periodAmount: string;
  /** periodAmount normalized to a monthly figure. */
  monthlyAmount: string;
  /** ISO timestamp when the contract entered SUSPENDED; null when unknown / not suspended. */
  suspendedAt?: string | null;
  /** Whole days suspended as of the response `asOf`; null when suspendedAt is unknown. */
  suspensionDays?: number | null;
  /**
   * True when suspensionDays >= tenant suspensionChurnThresholdDays.
   * Flag only — suspended value still reports in the suspended-MRR bucket here;
   * churn classification is owned by ALC-1428.
   */
  pastSuspensionChurnThreshold: boolean;
  /**
   * Projected usage contribution for the next cycle (variable charge only — never
   * includes committed baseFee). Null when the item is unmetered, suspended, or
   * usage confidence is UNAVAILABLE (never an assumed zero).
   */
  projectedUsagePeriodAmount?: string | null;
  /** projectedUsagePeriodAmount normalized to monthly. */
  projectedUsageMonthlyAmount?: string | null;
  /**
   * Confidence of the projected-usage component (ALC-1429 / ALC-1426).
   * Null when the item has no calculationMode (fixed recurring).
   */
  usageConfidence?: UsageProjectionConfidence | null;
  /** FINALIZED observations used for the projection; null when unmetered. */
  usageObservationCount?: number | null;
}

export interface BillingSaasMetricsCurrencyTotals {
  currency: string;
  /** Fixed recurring MRR from ACTIVE contracts only. */
  committedMrr: string;
  /** committedMrr × 12. */
  committedArr: string;
  /** Fixed recurring MRR from SUSPENDED contracts only (never mixed into committed). */
  suspendedMrr: string;
  /**
   * committedMrr + ACTIVE projected-usage MRR (ALC-1429). Always labeled distinctly
   * from committed — never leaks into committedMrr.
   */
  forecastMrr: string;
  /** forecastMrr × 12. */
  forecastArr: string;
  /**
   * Roll-up of usageConfidence across ACTIVE metered sources (worst-wins / UNAVAILABLE
   * caps at LOW when mixed). Null when no metered items contribute.
   */
  forecastUsageConfidence?: UsageProjectionConfidence | null;
}

/**
 * Committed MRR/ARR + suspended-MRR bucket (ALC-1419) and forecast MRR/ARR with
 * projected usage (ALC-1429). One-time and milestone amounts are excluded from both.
 */
export interface BillingSaasMetricsResponse {
  /** `YYYY-MM-DD` the metrics were computed as of. */
  asOf: string;
  /** Present only when the listing was filtered to one entity. */
  entityId?: string;
  /** Present only when the listing was filtered to one currency. */
  currency?: string;
  /** Tenant setting; default 90. Consumed by MRR movements (ALC-1428). */
  suspensionChurnThresholdDays: number;
  /**
   * Tenant lookback used for projected usage (from BILLING_AR_FORECAST_SETTINGS).
   * Default 3; clamped 1–24.
   */
  usageForecastLookbackPeriods: number;
  /** Totals grouped by currency (contracts may span currencies across entities). */
  currencies: BillingSaasMetricsCurrencyTotals[];
  /** Per-item sources that sum to the currency totals. */
  sources: BillingSaasMetricsSource[];
}

/**
 * Classified change in committed recurring value (ALC-1428).
 * Computed on read from Contract Version + status-history events — not persisted.
 */
export const MrrMovementType = {
  EXPANSION: 'EXPANSION',
  CONTRACTION: 'CONTRACTION',
  CHURN: 'CHURN',
  SUSPENSION: 'SUSPENSION',
  REACTIVATION: 'REACTIVATION',
} as const;
export type MrrMovementType = (typeof MrrMovementType)[keyof typeof MrrMovementType];

/**
 * What triggered the movement. `RENEWAL` is reserved for ALC-1423 dated Renewal
 * events: price deltas while status stays ACTIVE classify as expansion/contraction
 * only — never churn-plus-expansion.
 */
export const MrrMovementCause = {
  ACTIVATION: 'ACTIVATION',
  AMENDMENT: 'AMENDMENT',
  RENEWAL: 'RENEWAL',
  STATUS_TRANSITION: 'STATUS_TRANSITION',
  SUSPENSION_THRESHOLD: 'SUSPENSION_THRESHOLD',
} as const;
export type MrrMovementCause = (typeof MrrMovementCause)[keyof typeof MrrMovementCause];

/** One classified movement with traceability to the underlying event. */
export interface BillingMrrMovement {
  /** `YYYY-MM-DD` the movement became effective. */
  effectiveDate: string;
  movementType: MrrMovementType;
  cause: MrrMovementCause;
  contractId: string;
  contractCode: string;
  entityId: string;
  customerId: string;
  currency: string;
  /**
   * Absolute monthly magnitude (always ≥ 0). Use `committedImpact` for the
   * signed contribution to the committed-MRR waterfall.
   */
  monthlyAmount: string;
  /**
   * Signed contribution to committed MRR:
   * expansion/reactivation positive; contraction/churn/suspension negative;
   * suspension→churn reclassification is `0.00` (value already left committed).
   */
  committedImpact: string;
  /** Version in force after the movement, when applicable. */
  versionId?: string | null;
  /** Approved amendment that produced the version delta, when applicable. */
  amendmentId?: string | null;
  /** Status-history row for lifecycle transitions, when applicable. */
  statusHistoryId?: string | null;
}

export interface BillingMrrMovementsCurrencyTotals {
  currency: string;
  expansion: string;
  contraction: string;
  churn: string;
  suspension: string;
  reactivation: string;
  /** Sum of `committedImpact` — equals committedMrr(to) − committedMrr(from). */
  committedImpact: string;
  committedMrrFrom: string;
  committedMrrTo: string;
}

/**
 * MRR movements over a period (ALC-1428).
 * Invariant: for each currency, `committedImpact === committedMrrTo − committedMrrFrom`.
 */
export interface BillingMrrMovementsResponse {
  /**
   * Period start (`YYYY-MM-DD`), exclusive — movements after this date are included
   * so `sum(committedImpact)` equals committedMrr(to) − committedMrr(from).
   */
  from: string;
  /** Period end (`YYYY-MM-DD`), inclusive — movements on this date are included. */
  to: string;
  entityId?: string;
  currency?: string;
  suspensionChurnThresholdDays: number;
  currencies: BillingMrrMovementsCurrencyTotals[];
  movements: BillingMrrMovement[];
}

// ---------------------------------------------------------------------------
// Ciclo de receita (ALC-5089)
// ---------------------------------------------------------------------------

export interface RevenueCycleResponse {
  revenueCycleId: string;
  name: string;
  description: string | null;
  /** When the nota fiscal is issued: at approval (today's flow) or after the receipt. */
  nfTrigger: RevenueCycleNfTrigger;
  /** Accumulated lines (`invoicing = PERIOD_AGGREGATE`) are billed in one invoice per period. */
  aggregateAccumulated: boolean;
  /** The period's invoice only gathers transactions already reconciled. Requires aggregation. */
  aggregateOnlyReconciled: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRevenueCycleRequest {
  name: string;
  description?: string | null;
  nfTrigger?: RevenueCycleNfTrigger;
  aggregateAccumulated?: boolean;
  aggregateOnlyReconciled?: boolean;
}

export interface UpdateRevenueCycleRequest extends Partial<CreateRevenueCycleRequest> {
  isActive?: boolean;
}

/** A cycle as a contract or an invoice names it. `name` is null for the system Padrão cycle. */
export interface RevenueCycleRef {
  revenueCycleId: string | null;
  name: string | null;
}

/** A resolved cycle and where it came from (contract override, template default, or Padrão). */
export interface ResolvedRevenueCycle extends RevenueCycleRef {
  source: RevenueCycleSource;
  nfTrigger: RevenueCycleNfTrigger;
  aggregateAccumulated: boolean;
  aggregateOnlyReconciled: boolean;
}

/** The cycle fields every contract response carries (ALC-5089, D20). */
export interface BillingContractRevenueCycleFields {
  revenueCycle: { revenueCycleId: string; name: string } | null;
  effectiveRevenueCycle: ResolvedRevenueCycle;
}

/** Changes an ACTIVE or DRAFT contract's own cycle. Only invoices created afterwards follow it. */
export interface SetContractRevenueCycleRequest {
  /** Null clears the override: the contract follows its template's cycle, else Padrão. */
  revenueCycleId: string | null;
}
